/**
 * A wrapper for `JSON.parse()` which supports the return value of `JSON.stringify(undefined)`
 * which returns the string `"undefined"` and this method returns the value `undefined`.
 */
export const tryParseJSON = <T>(value: string | null): LocalStorageItem<T> => {
    if (value) {
        try {
            return value === 'undefined' ? undefined : JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value;
};

/**
 * A item value that can be stored in local storage
 */
export type LocalStorageItem<T> = string | undefined | null | T;

/**
 * The detail of a LocalStorageChange event
 */
interface LocalStorageChangeDetail {
    key: unknown;
    newValue: unknown;
}

/**
 * An event representing a change to a value in local storage that occurred withing the current tab, window, frame, etc.
 */
export class LocalStorageChangeEvent extends CustomEvent<LocalStorageChangeDetail> {
    constructor({ key, newValue }: LocalStorageChangeDetail) {
        super('localStorageChange', { detail: { key, newValue } });
    }

    static dispatch(eventData: LocalStorageChangeDetail) {
        window.dispatchEvent(new LocalStorageChangeEvent(eventData));
    }
}

/**
 * Extends WindowEventMap to include LocalStorageChangeEvent
 */
declare global {
    interface WindowEventMap {
        localStorageChange: LocalStorageChangeEvent;
    }
}

/**
 * A stored item with a defined TTL.
 */
interface StoredItemWithTtl<T> {
    expiry: number;
    value: T;
}

/**
 * Checks if the given object is a StoredItemWithTtl.
 */
function isTtlItem<T>(obj: any): obj is StoredItemWithTtl<T> {
    return obj && typeof obj.expiry === 'number';
}

/**
 * Represents an in-memory data store used as a fallback when localstorage is unavailable.
 */
export const inMemoryData = new Map<string, unknown>();

/**
 * Set a JSON-serialized value in the local storage with an optional time-to-live (TTL) in ms for expiration.
 */
export const setLocalStorageItem = <T>(
    key: string,
    value: T,
    ttl: number | undefined = undefined
): void => {
    let item: T | StoredItemWithTtl<T> = value;
    if (ttl) {
        const now = new Date();
        item = {
            value: value,
            expiry: now.getTime() + ttl,
        };
    }
    // reasons for `localStorage` to throw an error:
    // - maximum quota is exceeded
    // - under Mobile Safari (since iOS 5) when the user enters private mode
    //   `localStorage.setItem()` will throw
    // - trying to access localStorage object when cookies are disabled in Safari throws
    //   "SecurityError: The operation is insecure."
    try {
        localStorage.setItem(key, JSON.stringify(item));

        inMemoryData.delete(key);
    } catch {
        inMemoryData.set(key, item);
    }
    LocalStorageChangeEvent.dispatch({ key, newValue: value });
};

/**
 * Retrieves an JSON-serialized item from the local storage based on a given key. If the key has an optional
 * TTL set, this will remove the key if expired.
 */
export const getLocalStorageItem = <T>(key: string): LocalStorageItem<T> => {
    let item: LocalStorageItem<T>;
    if (inMemoryData.has(key)) {
        item = inMemoryData.get(key) as T;
    } else {
        try {
            item = tryParseJSON(localStorage.getItem(key));
        } catch {
            // If the key is not on inMemory storage and localstorage is
            // unavailable, return a default value of null.
            return null;
        }
    }
    if (!isTtlItem<LocalStorageItem<T>>(item)) {
        return item;
    }
    const now = new Date();

    // compare the expiry time of the item with the current time
    if (now.getTime() > item.expiry) {
        // If the item is expired, delete the item from storage
        // and return null
        removeLocalStorageItem(key);
        return null;
    }
    return item.value;
};

/**
 * Removes an item from the localStorage based on the provided key.
 */
export const removeLocalStorageItem = (key: string): void => {
    inMemoryData.delete(key);
    localStorage.removeItem(key);
    LocalStorageChangeEvent.dispatch({ key, newValue: undefined });
};

/**
 * Update the time-to-live (TTL) of a local storage item.
 */
export const updateLocalStorageItemTtl = (key: string, ttl: number): void => {
    const value = getLocalStorageItem(key);
    if (value !== null) {
        setLocalStorageItem(key, value, ttl);
    }
};

export const removeAllExpiredItems = (): void => {
    try {
        const keys = Object.keys(localStorage);
        const now = new Date().getTime();
        for (const key of keys) {
            try {
                const item = JSON.parse(localStorage.getItem(key) || '');
                if (item['expiry']) {
                    if (now > item.expiry) {
                        removeLocalStorageItem(key);
                    }
                }
            } catch (_er) {
                // Ignore, best effort to remove valid local storage items
            }
        }
    } catch (_error) {
        // Ignore, for ex. no local storage access
    }
};
